// API client for the appointment-request system.
//
// Every call carries the Supabase access token as a Bearer header; the backend
// derives identity from that token and never from the body, because these
// endpoints write into a clinic's schedule. Same shape as
// components/patient/patientPortalApi.js — see the note there.
//
// `err.code` is preserved from the JSON body so callers can branch rather than
// showing one generic failure: `slot_taken` means "refetch the grid, the world
// moved", and `appointments_not_set_up` means the clinic hasn't run the
// migration yet. Neither is "something went wrong".
import { getSupabase } from "../components/supabaseClient";
import { getBackendURL } from "../components/env";

async function authHeader() {
  const sb = await getSupabase();
  const {
    data: { session },
  } = await sb.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("You're signed out. Please sign in again.");
  return `Bearer ${token}`;
}

async function readError(res, fallback) {
  let body = null;
  try {
    body = await res.json();
  } catch (_) {
    /* non-JSON error body (proxy/HTML) — fall through to the generic text */
  }
  const err = new Error(body?.reason || body?.error || fallback);
  err.code = body?.error || null;
  err.status = res.status;
  return err;
}

async function call(path, { method = "GET", body, query } = {}) {
  // URLSearchParams encodes for us, so a schema named "test 122" survives.
  // Blank values are dropped rather than sent as `status=`, which the backend
  // would otherwise have to distinguish from "no filter".
  const pairs = Object.entries(query || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  const qs = pairs.length ? `?${new URLSearchParams(pairs).toString()}` : "";
  const res = await fetch(`${getBackendURL()}/api/appointments/${path}${qs}`, {
    method,
    headers: {
      Authorization: await authHeader(),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw await readError(res, "That didn't go through.");
  return res.json();
}

// ── Patient ────────────────────────────────────────────────────────────────

/** Doctors in this patient's clinic group. Schemas with no physician_name are
 *  omitted by the backend, so an empty list means "nobody is bookable yet". */
export const fetchDoctors = () => call("doctors/").then((d) => d.doctors || []);

/** { slots_by_date: { '2026-08-11': ['09:00', …] }, timezone } */
export const fetchSlots = ({ schema, from, to }) =>
  call("slots/", { query: { schema, from, to } });

export const createRequest = ({ schema, slotDate, slotTime, note }) =>
  call("requests/", {
    method: "POST",
    body: { schema, slot_date: slotDate, slot_time: slotTime, note },
  }).then((d) => d.request);

export const fetchMyRequests = () =>
  call("requests/mine/").then((d) => d.requests || []);

export const cancelRequest = (id, schema) =>
  call(`requests/${id}/cancel/`, { method: "POST", body: { schema } }).then(
    (d) => d.request
  );

// ── Clinic ─────────────────────────────────────────────────────────────────

export const fetchConfig = (schema) => call("config/", { query: { schema } });

export const saveConfig = ({
  schema,
  slotMinutes,
  dayStart,
  dayEnd,
  defaultCapacity,
  timezone,
}) =>
  call("config/", {
    method: "PUT",
    body: {
      schema,
      slot_minutes: slotMinutes,
      day_start: dayStart,
      day_end: dayEnd,
      default_capacity: defaultCapacity,
      // The clinic's own zone — what "9:00" means here. Distinct from any
      // user's timezone; notes-c rows are converted INTO this one.
      timezone,
    },
  });

export const fetchAvailability = (schema) =>
  call("availability/", { query: { schema } }).then((d) => d.rules || []);

export const createRule = ({ schema, dayOfWeek, startTime, endTime }) =>
  call("availability/", {
    method: "POST",
    body: {
      schema,
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
    },
  }).then((d) => d.rules || []);

/**
 * Replace the whole weekly pattern in one call.
 *
 * `days` is 0=Sunday…6=Saturday. `endTime` is when the day FINISHES, not when
 * the last appointment starts — at a 15-minute grid, 17:00 means the last
 * booking is 16:45.
 */
export const applyStandardWeek = ({
  schema,
  days,
  startTime,
  endTime,
  lunchStart,
  lunchEnd,
}) =>
  call("availability/standard-week/", {
    method: "POST",
    body: {
      schema,
      days,
      start_time: startTime,
      end_time: endTime,
      lunch_start: lunchStart || null,
      lunch_end: lunchEnd || null,
    },
  }).then((d) => d.rules || []);

/**
 * Edit ONE window: shorten, extend, move to another day, or split it around a
 * break. A break replaces the window with two, so the returned list can be one
 * longer than it went in.
 */
export const updateRule = ({
  schema,
  id,
  dayOfWeek,
  startTime,
  endTime,
  breakStart,
  breakEnd,
}) =>
  call("availability/", {
    method: "PUT",
    body: {
      schema,
      id,
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
      break_start: breakStart || null,
      break_end: breakEnd || null,
    },
  }).then((d) => d.rules || []);

export const deleteRule = (schema, id) =>
  call("availability/", { method: "DELETE", body: { schema, id } }).then(
    (d) => d.rules || []
  );

export const fetchOverrides = (schema) =>
  call("overrides/", { query: { schema } }).then((d) => d.overrides || []);

export const createOverride = ({
  schema,
  slotDate,
  dayOfWeek,
  slotTime,
  slotTimeEnd,
  capacity,
  reason,
}) =>
  call("overrides/", {
    method: "POST",
    body: {
      schema,
      // Exactly one of these may be set — the backend 400s otherwise, and the
      // table's CHECK constraint refuses it below that.
      slot_date: slotDate || null,
      day_of_week: slotDate ? null : dayOfWeek,
      slot_time: slotTime,
      // A range expands server-side into one override per slot, on the
      // clinic's own grid. Omit for a single slot.
      slot_time_end: slotTimeEnd || null,
      capacity,
      reason,
    },
  }).then((d) => d.overrides || []);

export const deleteOverride = (schema, id) =>
  call("overrides/", { method: "DELETE", body: { schema, id } }).then(
    (d) => d.overrides || []
  );

// ── Time off ───────────────────────────────────────────────────────────────
// Whole days closed, as a RANGE. Per-slot overrides would need 160 rows to
// express a week away at the default 15-minute grid.

export const fetchBlockedDates = (schema) =>
  call("blocked-dates/", { query: { schema } }).then(
    (d) => d.blocked_dates || []
  );

/** `toDate` defaults to `fromDate`, so a single day off needs only one date. */
export const createBlockedDates = ({ schema, fromDate, toDate, reason }) =>
  call("blocked-dates/", {
    method: "POST",
    body: {
      schema,
      from_date: fromDate,
      to_date: toDate || fromDate,
      reason,
    },
  }).then((d) => d.blocked_dates || []);

export const deleteBlockedDates = (schema, id) =>
  call("blocked-dates/", { method: "DELETE", body: { schema, id } }).then(
    (d) => d.blocked_dates || []
  );

export const fetchRequests = ({ schema, status }) =>
  call("requests/list/", { query: { schema, status } }).then(
    (d) => d.requests || []
  );

export const approveRequest = (id, { schema, notesCRowId }) => {
  // Guarded here as well as on the server. An approve fired without the row id
  // would 400 AFTER the admin had already filled in and closed the modal, and
  // they'd have to type it all again to find out why nothing happened.
  if (!notesCRowId) {
    return Promise.reject(
      new Error(
        "Save the appointment first — approval needs the row it created."
      )
    );
  }
  return call(`requests/${id}/approve/`, {
    method: "POST",
    body: { schema, notes_c_row_id: notesCRowId },
  }).then((d) => d.request);
};

export const denyRequest = (id, { schema, reason, block = "date" }) =>
  call(`requests/${id}/deny/`, {
    method: "POST",
    body: { schema, reason, block },
  });
